import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { IconAlertTriangle, IconCheck, IconSparkles } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { serverMessage } from '../../utils/serverMessage'
import classes from './V2Form.module.css'

const EXPECTED_SECONDS = 25

// One sentence per verdict level, as the service reports it (AD-152).
const VERDICT_TITLE = {
  good: 'editorV2.form.removeText.verdictGood',
  warn: 'editorV2.form.removeText.verdictWarn',
  suspect: 'editorV2.form.removeText.verdictSuspect',
  bad: 'editorV2.form.removeText.verdictBad',
  unknown: 'editorV2.form.removeText.verdictUnknown',
}

/** The picture's bytes: our own and browser-only images directly, other hosts through our origin. */
async function pictureFile(url) {
  const own = /^(blob:|data:)/.test(url) || new URL(url, window.location.href).origin === window.location.origin
  const response = await fetch(
    own ? url : `/api/studio-editor-v2/media-library/fetch?url=${encodeURIComponent(url)}`,
    { credentials: 'same-origin' },
  )
  if (!response.ok) throw new Error(i18n.t('editorV2.form.removeText.readFailed'))
  const blob = await response.blob()
  return new File([blob], 'picture', { type: blob.type || 'image/jpeg' })
}

/**
 * "Remove text" (AD-152): takes the advertiser's text and overlays off the current slide
 * picture. It takes ~25 s and makes a new file, so it never blocks the rest of the editor
 * and never replaces anything: the result becomes the current picture and both it and the
 * original sit in the Advertiser Library as tiles, so "back" is one click.
 */
export function useRemoveText({ media, sourceUrl, onChange, adoptUrl, configuration, currentUrl }) {
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState(0)
  const [outcome, setOutcome] = useState(null)
  const [variant, setVariant] = useState(null)
  const [dismissed, setDismissed] = useState(false)
  const awaitingResult = useRef(null)

  // The result is imported as a new upload; its media reference exists once that lands.
  useEffect(() => {
    if (awaitingResult.current && media.sourceAssetId
      && media.sourceAssetId !== awaitingResult.current.original.sourceAssetId) {
      const pending = awaitingResult.current
      awaitingResult.current = null
      setVariant({ ...pending, result: media })
    }
  }, [media])

  useEffect(() => {
    if (!running) return undefined
    const started = Date.now()
    const timer = setInterval(() => {
      setProgress(Math.min(96, ((Date.now() - started) / 1000 / EXPECTED_SECONDS) * 100))
    }, 250)
    return () => clearInterval(timer)
  }, [running])

  const textIds = configuration?.suggestions?.textInImage || []
  const flagged = Boolean(media.sourceAssetId) && (
    textIds.includes(media.sourceAssetId)
    || textIds.some((id) => currentUrl && configuration?.assets?.[id]?.url === currentUrl)
  )

  const run = async () => {
    if (running || !sourceUrl) return
    setRunning(true)
    setProgress(0)
    setOutcome(null)
    const original = media
    const originalUrl = sourceUrl
    try {
      const body = new FormData()
      body.append('image', await pictureFile(originalUrl))
      const response = await axios.post('/api/studio-editor-v2/images/remove-text', body)
      const data = response.data?.data || {}
      if (data.error) {
        setOutcome({ level: 'error', title: i18n.t('editorV2.form.removeText.failedTitle'), text: [data.error, data.text].filter(Boolean).join(' ') })
        return
      }
      if (data.aspect_changed) {
        setOutcome({ level: 'bad', title: i18n.t(VERDICT_TITLE.bad), text: data.verdict?.text || '' })
        return
      }
      awaitingResult.current = { original, originalUrl, resultUrl: data.image }
      // Only a result that became the picture is reported as done; otherwise the next
      // picture change must not be taken for it.
      if (!await adoptUrl(data.image, 'without-text.png', { source: 'ai' })) {
        awaitingResult.current = null
        setOutcome({ level: 'error', title: i18n.t('editorV2.form.removeText.failedTitle'), text: i18n.t('editorV2.form.removeText.importFailed') })
        return
      }
      const level = data.verdict?.level || 'unknown'
      setOutcome({
        level,
        title: i18n.t(VERDICT_TITLE[level] || VERDICT_TITLE.unknown),
        text: [data.verdict?.text, i18n.t('editorV2.form.removeText.originalKept')].filter(Boolean).join(' '),
      })
      setDismissed(true)
    } catch (error) {
      awaitingResult.current = null
      const message = serverMessage(error?.response?.data?.message) || error?.message
        || i18n.t('editorV2.form.removeText.unreachable')
      setOutcome({ level: 'error', title: i18n.t('editorV2.form.removeText.failedTitle'), text: message })
    } finally {
      setRunning(false)
    }
  }

  // Tiles the library shows first: the result and the picture it was made from.
  const tiles = variant ? [
    { key: 'result', url: variant.resultUrl, tag: i18n.t('editorV2.form.removeText.tagNoText'), media: variant.result },
    { key: 'original', url: variant.originalUrl, tag: i18n.t('editorV2.form.removeText.tagOriginal'), media: variant.original },
  ] : []

  return {
    running,
    progress,
    outcome,
    run,
    cue: flagged && !dismissed && !running && !outcome,
    tiles,
    pickTile: (tile) => { if (tile.media) onChange(tile.media) },
    isCurrent: (tile) => Boolean(tile.media?.sourceAssetId) && tile.media.sourceAssetId === media.sourceAssetId,
  }
}

export function RemoveTextButton({ removeText }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      className={classes.smallButton}
      disabled={removeText.running}
      onClick={removeText.run}
      title={t('editorV2.form.removeText.buttonTitle', { seconds: EXPECTED_SECONDS })}
    >
      <IconSparkles size={14} stroke={1.8} />
      {removeText.running ? t('editorV2.form.removeText.running') : t('editorV2.form.removeText.button')}
    </button>
  )
}

export function RemoveTextProgress({ removeText }) {
  const { t } = useTranslation()
  if (!removeText.running) return null
  return (
    <div className={classes.aiShim} role="status">
      <span>{t('editorV2.form.removeText.progress')}</span>
      <span className={classes.aiBar}><i style={{ width: `${removeText.progress}%` }} /></span>
      <span className={classes.aiEta}>{t('editorV2.form.removeText.eta', { seconds: EXPECTED_SECONDS })}</span>
    </div>
  )
}

export function RemoveTextNotes({ removeText }) {
  const { outcome } = removeText
  return (
    <div className={classes.aiNote} data-level={outcome.level}>
      {outcome.level === 'good' ? <IconCheck size={14} stroke={2} /> : <IconAlertTriangle size={14} stroke={1.8} />}
      <span><b>{outcome.title}</b> {outcome.text}</span>
    </div>
  )
}
